import { MoreSmall } from "@wix/wix-ui-icons-common";
import classNames from "classnames";
import React, { useContext, useEffect, useRef, useState } from "react";
import { RicosContext } from "./../../ricos-context/index.js";
import { ReactPlayer } from "./../../wix-react-player/index.js";
import {
  DownloadIcon,
  Image,
  PlaybackIcon,
  Slider,
  SLIDER_THUMB_VISIBILITY,
  SLIDER_TRACK_SIZE
} from "./../../ui-components/index.js";
import { IconButton } from "../components/icon-button.js";
import {
  AudioActionIcons,
  AudioDetails,
  AudioDuration,
  ContextMenu,
  SoundControl
} from "../components/index.js";
import {
  AUDIO_ACTION_NAMES,
  AUDIO_BI_VALUES,
  AUDIO_TYPES,
  downloadFile,
  playbackRates
} from "../consts.js";
import { AUDIO_CONTEXT_MENU, AUDIO_SLIDER } from "../data-hooks.js";
import styles from "../statics/styles/audio-scss.js";
import { AUDIO_TYPE } from "../types.js";
const ReactPlayerWrapper = ({
  url,
  authorName,
  name,
  disableDownload,
  coverImage,
  helpers,
  disabled,
  isLoading,
  saveDurationToData
}) => {
  const { tabIndex, t, isMobile } = useContext(RicosContext);
  const [URL, setURL] = useState(url);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(1);
  const [lastVolume, setLastVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showPlaybackMenu, setShowPlaybackMenu] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isStoppedBySeek, setIsStoppedBySeek] = useState(false);
  const slimDesign = !authorName && !name;
  const reactPlayerRef = useRef(null);
  const onDownload = () => downloadFile(URL, name || "untitled");
  const playBackMenuData = playbackRates.map(({ text, rate }) => ({
    text,
    onClick: () => {
      setPlaybackRate(rate);
      setShowPlaybackMenu(false);
    },
    selected: playbackRate === rate
  }));
  const contextMenuOptions = {
    download: {
      text: "Download",
      onClick: () => {
        helpers?.onViewerAction?.(
          AUDIO_TYPE,
          AUDIO_ACTION_NAMES.fileDownloaded,
          AUDIO_BI_VALUES.click
        );
        onDownload();
        setShowContextMenu(false);
      },
      dataHook: "audio-download-icon",
      icon: DownloadIcon
    },
    playback: {
      text: "Playback Speed",
      onClick: () => {
        setShowContextMenu(false);
        setShowPlaybackMenu(true);
      },
      dataHook: "audio-playback-icon",
      icon: PlaybackIcon
    }
  };
  const contextMenuData = disableDownload ? [contextMenuOptions.playback] : [contextMenuOptions.download, contextMenuOptions.playback];
  const handleProgress = (state) => {
    if (!seeking) {
      setPlayed(state.played);
    }
  };
  const onEnded = () => helpers?.onViewerAction?.(
    AUDIO_TYPE,
    AUDIO_ACTION_NAMES.playAudio,
    AUDIO_BI_VALUES.PLAYBACK_COMPLETED
  );
  const handleDuration = (num) => {
    setDuration(num);
    saveDurationToData(num);
    helpers?.mediaPluginsDetails?.({
      pluginId: AUDIO_TYPE,
      creator: authorName,
      title: name,
      track_duration: num?.toString(),
      type: AUDIO_TYPES.custom,
      url
    });
  };
  const handleSeekChange = (num) => {
    isPlaying && setIsStoppedBySeek(true);
    handlePause();
    setSeeking(true);
    setPlayed(num);
  };
  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleContextMenuClick = () => {
    showContextMenu || showPlaybackMenu ? onClickOutside() : setShowContextMenu(true);
  };
  const handleSeekMouseUp = (num) => {
    reactPlayerRef?.current?.seekTo(num);
    setSeeking(false);
    isStoppedBySeek && handlePlay();
    setIsStoppedBySeek(false);
  };
  const handleMute = () => {
    if (volume !== 0) {
      setLastVolume(volume);
      setVolume(0);
    } else {
      setVolume(lastVolume);
    }
  };
  const onClickOutside = () => {
    setShowContextMenu(false);
    setShowPlaybackMenu(false);
  };
  useEffect(() => {
    handlePause();
    setURL(url);
  }, [url]);
  useEffect(() => {
    reactPlayerRef.current?.seekTo(0);
  }, []);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.customPlayer, { [styles.slim]: slimDesign }) }, coverImage?.src && /* @__PURE__ */ React.createElement(
    Image,
    {
      containerProps: { className: styles.coverImageContainer },
      className: styles.coverImage,
      uri: coverImage.src,
      width: 350,
      height: 350,
      displayMode: "fill",
      alt: t("Audio_Plugin_Cover_Alt")
    }
  ), /* @__PURE__ */ React.createElement("div", { className: styles.trackDetailsContainer }, !slimDesign && /* @__PURE__ */ React.createElement(AudioDetails, { authorName, title: name }), /* @__PURE__ */ React.createElement("div", { className: styles.controlsContainer }, /* @__PURE__ */ React.createElement("div", { className: classNames(styles.controlsGroup, styles.sliderContainer) }, /* @__PURE__ */ React.createElement(
    AudioActionIcons,
    {
      handlePlay,
      handlePause,
      isPlaying,
      disabled,
      isLoading,
      helpers
    }
  ), /* @__PURE__ */ React.createElement(
    Slider,
    {
      languageDir: "ltr",
      value: played,
      dataHook: AUDIO_SLIDER,
      onChange: handleSeekChange,
      onChangeCommitted: handleSeekMouseUp,
      step: "any",
      min: 0,
      max: 1,
      thumbVisibility: SLIDER_THUMB_VISIBILITY.hover,
      trackSize: SLIDER_TRACK_SIZE.small,
      tabIndex,
      disabled: isLoading,
      ariaLabel: t("Audio_Plugin_Elapsed_Time_Slider_AriaLabel"),
      ariaProps: {
        "aria-valuenow": played,
        "aria-valuetext": `${formatTime(played * duration)} of ${formatTime(duration)}`
      }
    }
  ), /* @__PURE__ */ React.createElement(AudioDuration, { isLoading, played, duration })), /* @__PURE__ */ React.createElement("div", { className: styles.controlsGroup }, !isMobile && /* @__PURE__ */ React.createElement(
    SoundControl,
    {
      volume,
      handleMute,
      muted: volume === 0,
      handleVolumeChange: setVolume,
      disabled: isLoading
    }
  ), /* @__PURE__ */ React.createElement(
    IconButton,
    {
      tabIndex,
      isLoading,
      onClick: handleContextMenuClick,
      "aria-label": t("Audio_Plugin_More_Actions_Button_AriaLabel"),
      "data-hook": AUDIO_CONTEXT_MENU,
      "aria-haspopup": "true",
      icon: /* @__PURE__ */ React.createElement(MoreSmall, { className: styles.audio_contextMenuIcon })
    },
    /* @__PURE__ */ React.createElement(
      ContextMenu,
      {
        "data-hook": AUDIO_CONTEXT_MENU,
        onClickOutside,
        contextMenuData,
        playBackMenuData,
        showContextMenu,
        showPlaybackMenu
      }
    )
  ))))), /* @__PURE__ */ React.createElement(
    ReactPlayer,
    {
      previewAriaLabel: "Play audio",
      url: URL,
      ref: reactPlayerRef,
      playing: disabled ? false : isPlaying,
      playbackRate,
      onPause: handlePause,
      onPlay: handlePlay,
      width: 0,
      height: 0,
      controls: false,
      volume,
      onProgress: handleProgress,
      onDuration: handleDuration,
      "data-loaded": !isLoading,
      onEnded,
      config: {
        file: {
          forceAudio: true
        }
      }
    }
  ));
};
const formatTime = (totalSeconds) => {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const secondsStr = seconds.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");
  if (hours > 0) {
    return `${hours}:${minutesStr}:${secondsStr}`;
  } else {
    return `${minutes}:${secondsStr}`;
  }
};
export {
  ReactPlayerWrapper
};
//# sourceMappingURL=react-player-wrapper.js.map
